import { useMemo, useState, useCallback } from 'react';
import type { MenuProps } from '@coreai/fluentui-react';

/** Internal: case-insensitive substring filter on a labelled list. */
function filterBySearch<T>(
  items: readonly T[],
  getLabel: (item: T) => string,
  query: string,
): readonly T[] {
  const trimmed = query.trim().toLowerCase();
  if (!trimmed) return items;
  return items.filter((item) => getLabel(item).toLowerCase().includes(trimmed));
}

/**
 * Combined search + sort state for the header pill menus.
 *
 * All three pills (Workspace, App, Workflow) share an identical UX:
 *  - pin the user's current selection to the top of the list,
 *  - sort the remaining items alphabetically by their display label,
 *  - filter live as the user types.
 *
 * The visible list always contains every match (or every item when not
 * searching). Vertical bounding is handled by the scrollable container in
 * `PillMenu` — overflow rows are reachable via scroll rather than a "+N more"
 * jump. `isSearching` is surfaced so the caller can distinguish "empty
 * because there are no items" from "empty because the search matched
 * nothing" in its empty-state copy.
 *
 * The `isCurrent` predicate (rather than a single-string match) is what
 * identifies the pinned row. Callers can match on a composite key — for
 * example the workspace pill matches on (name + region) so two projects
 * with the same name in different regions don't collide.
 *
 * `onMenuOpenChange` is a small convenience: it clears the search query
 * when the menu closes. Bind it directly to `<Menu onOpenChange={...}>`.
 */
export function useMenuList<T>(
  items: readonly T[],
  getLabel: (item: T) => string,
  isCurrent: (item: T) => boolean,
): {
  search: string;
  setSearch: (value: string) => void;
  visible: readonly T[];
  isSearching: boolean;
  onMenuOpenChange: NonNullable<MenuProps['onOpenChange']>;
} {
  const sorted = useMemo(() => {
    const current = items.find(isCurrent);
    const rest = items
      .filter((item) => !isCurrent(item))
      .sort((a, b) => getLabel(a).localeCompare(getLabel(b)));
    return current ? [current, ...rest] : rest;
  }, [items, getLabel, isCurrent]);

  const [search, setSearch] = useState('');
  const visible = filterBySearch(sorted, getLabel, search);
  const isSearching = search.trim().length > 0;

  const onMenuOpenChange = useCallback<NonNullable<MenuProps['onOpenChange']>>(
    (_e, data) => {
      if (!data.open) setSearch('');
    },
    [],
  );

  return { search, setSearch, visible, isSearching, onMenuOpenChange };
}
